import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';
import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

const TopCustomers = () => {
    const { companyId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('this_year');
    const [customersData, setCustomersData] = useState({
        customers: [],
        totalCompanyRevenue: 0,
        period: {}
    });

    const periodOptions = [
        { value: 'this_month', label: 'Denna månad' },
        { value: 'last_3_months', label: 'Senaste 3 månaderna' },
        { value: 'last_6_months', label: 'Senaste 6 månaderna' },
        { value: 'this_year', label: 'Detta år' },
        { value: 'last_12_months', label: 'Senaste 12 månaderna' }
    ];

    useEffect(() => {
        fetchTopCustomers();
    }, [companyId, selectedPeriod]);

    const fetchTopCustomers = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axiosInstance.get(
                getApiUrl(`${companyId}/dashboard/top-customers`),
                {
                    params: {
                        limit: 5,
                        period: selectedPeriod
                    },
                    withCredentials: true
                }
            );

            setCustomersData(response.data);
        } catch (error) {
            console.error('Error fetching top customers:', error);
            setError('Kunde inte ladda kunddata');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('sv-SE', {
            style: 'currency',
            currency: 'SEK',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getProgressGradient = (index) => {
        const gradients = [
            'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
        ];
        return gradients[index % gradients.length];
    };

    const getInitials = (name) => {
        return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2);
    };

    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
    };

    const getSelectedPeriodLabel = () => {
        const selected = periodOptions.find(option => option.value === selectedPeriod);
        return selected ? selected.label : 'Detta år';
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
                background: 'white',
                borderRadius: '16px',
                border: '1px solid #e2e8f0'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '1rem'
                }}>
                    <div style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #e2e8f0',
                        borderTop: '3px solid #3b82f6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <span style={{ color: '#64748b', fontSize: '0.875rem' }}>
                        Laddar kunddata...
                    </span>
                </div>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                borderRadius: '16px',
                border: '1px solid #fecaca'
            }}>
                <div style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#dc2626' }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="2" />
                        <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="2" />
                    </svg>
                </div>
                <p style={{ color: '#dc2626', fontSize: '0.875rem', margin: '0 0 1rem 0' }}>
                    {error}
                </p>
                <button
                    onClick={fetchTopCustomers}
                    style={{
                        padding: '0.5rem 1rem',
                        border: '1px solid #dc2626',
                        borderRadius: '8px',
                        background: 'white',
                        color: '#dc2626',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                    }}
                >
                    Försök igen
                </button>
            </div>
        );
    }

    return (
        <div style={{ background: 'white', borderRadius: '16px', padding: '1.5rem' }}>
            {/* Header with period selector */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h5 style={{ margin: 0, fontWeight: '600', color: '#1e293b' }}>Toppkunder</h5>
                    <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                        Dina mest värdefulla kunder
                    </p>
                </div>
                <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#4338ca' }}>
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                        <path d="M23 21V19C23 18.1645 22.7155 17.3541 22.2094 16.7107C21.7033 16.0673 20.0075 15.6228 19 15.3949" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16 3.13C17.0013 3.35715 17.6936 3.80298 18.2094 4.44561C18.7155 5.08825 19 5.89435 19 6.72507C19 7.55579 18.7155 8.36189 18.2094 9.00453C17.7033 9.64716 17.0075 10.093 16 10.32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>

            {/* Period Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
                <Dropdown>
                    <Dropdown.Toggle
                        variant="outline-secondary"
                        size="sm"
                        style={{
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            color: '#64748b',
                            fontSize: '0.875rem'
                        }}
                    >
                        {getSelectedPeriodLabel()}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {periodOptions.map(option => (
                            <Dropdown.Item
                                key={option.value}
                                onClick={() => handlePeriodChange(option.value)}
                                active={selectedPeriod === option.value}
                            >
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {customersData.customers.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    padding: '3rem 1rem',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    borderRadius: '16px',
                    border: '1px solid #e2e8f0'
                }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 1rem'
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-slate-400">
                            <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                        </svg>
                    </div>
                    <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
                        Inga kunder att visa för vald period
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    {customersData.customers.map((customer, index) => (
                        <div
                            key={customer.id}
                            style={{
                                background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
                                borderRadius: '16px',
                                padding: '1.5rem',
                                border: '1px solid #e2e8f0',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center">
                                    <div style={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: '12px',
                                        background: getProgressGradient(index),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '1rem',
                                        color: 'white',
                                        fontWeight: '600',
                                        fontSize: '0.875rem'
                                    }}>
                                        {getInitials(customer.name)}
                                    </div>
                                    <div>
                                        <h6 style={{
                                            margin: '0 0 0.25rem 0',
                                            fontWeight: '600',
                                            color: '#1e293b',
                                            fontSize: '0.875rem'
                                        }}>
                                            {customer.name}
                                        </h6>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: '#64748b',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            {customer.invoiceCount} fakturor
                                            {customer.lastInvoiceDate && (
                                                <>
                                                    <span style={{ color: '#cbd5e1' }}>•</span>
                                                    <span>Senast: {moment(customer.lastInvoiceDate).format('D MMM')}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="text-end">
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: '700',
                                        color: '#1e293b',
                                        marginBottom: '0.25rem'
                                    }}>
                                        {formatCurrency(customer.revenue)}
                                    </div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        fontWeight: '500',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '6px',
                                        background: `${getProgressGradient(index).split(',')[0].split('(')[1]}20`,
                                        color: getProgressGradient(index).split(',')[0].split('(')[1],
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {customer.percentage}% av total
                                    </div>
                                </div>
                            </div>

                            {/* Modern Progress Bar */}
                            <div style={{
                                width: '100%',
                                height: '8px',
                                background: '#f1f5f9',
                                borderRadius: '4px',
                                overflow: 'hidden',
                                marginBottom: '0.75rem'
                            }}>
                                <div style={{
                                    width: `${Math.min(customer.percentage, 100)}%`,
                                    height: '100%',
                                    background: getProgressGradient(index),
                                    borderRadius: '4px',
                                    transition: 'width 0.8s ease'
                                }} />
                            </div>

                            {/* Additional customer details */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.75rem',
                                color: '#64748b'
                            }}>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <span>
                                        <strong style={{ color: '#10b981' }}>{customer.paidInvoices}</strong> betalda
                                    </span>
                                    {customer.unpaidInvoices > 0 && (
                                        <span>
                                            <strong style={{ color: '#f59e0b' }}>{customer.unpaidInvoices}</strong> obetalda
                                        </span>
                                    )}
                                </div>
                                <div>
                                    Snitt: {formatCurrency(customer.avgInvoiceValue)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="text-center mt-4">
                <Link
                    to={`/${companyId}/sales/customers`}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.5rem',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                    </svg>
                    Visa alla kunder
                </Link>
            </div>
        </div>
    );
};

export default TopCustomers;



// import React, { useState, useEffect } from 'react';
// import { Link, useParams } from 'react-router-dom';
// import { ProgressBar } from 'react-bootstrap';
// import axiosInstance from '../../../../services/AxiosInstance';
// import { getApiUrl } from '../../../../utils/ApiUtils';

// const TopCustomers = () => {
//     const { companyId } = useParams();
//     const [customers, setCustomers] = useState([
//         {
//             id: '1',
//             name: 'Ework Group AB',
//             revenue: 125000,
//             percentage: 45,
//             invoiceCount: 8
//         },
//         {
//             id: '2',
//             name: 'ABC Consulting AB',
//             revenue: 85000,
//             percentage: 30,
//             invoiceCount: 5
//         },
//         {
//             id: '3',
//             name: 'Tech Solutions Sweden',
//             revenue: 65000,
//             percentage: 23,
//             invoiceCount: 4
//         },
//         {
//             id: '4',
//             name: 'Digital Innovation HB',
//             revenue: 45000,
//             percentage: 16,
//             invoiceCount: 3
//         },
//         {
//             id: '5',
//             name: 'Svenska Företag AB',
//             revenue: 35000,
//             percentage: 12,
//             invoiceCount: 2
//         }
//     ]);

//     useEffect(() => {
//         fetchTopCustomers();
//     }, [companyId]);

//     const fetchTopCustomers = async () => {
//         try {
//             // Replace with actual API call
//             // const response = await axiosInstance.get(getApiUrl(`${companyId}/dashboard/top-customers`));
//             // setCustomers(response.data);
//         } catch (error) {
//             console.error('Error fetching top customers:', error);
//         }
//     };

//     const formatCurrency = (amount) => {
//         return new Intl.NumberFormat('sv-SE', {
//             style: 'currency',
//             currency: 'SEK',
//             minimumFractionDigits: 0,
//             maximumFractionDigits: 0
//         }).format(amount);
//     };

//     const getProgressGradient = (index) => {
//         const gradients = [
//             'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
//             'linear-gradient(135deg, #10b981 0%, #059669 100%)',
//             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
//             'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
//             'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
//         ];
//         return gradients[index % gradients.length];
//     };

//     const getInitials = (name) => {
//         return name.split(' ').map(word => word[0]).join('').toUpperCase();
//     };

//     return (
//         <div style={{ background: 'white', borderRadius: '16px', padding: '1.5rem' }}>
//             <div className="d-flex align-items-center justify-content-between mb-4">
//                 <div>
//                     <h5 style={{ margin: 0, fontWeight: '600', color: '#1e293b' }}>Toppkunder</h5>
//                     <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
//                         Dina mest värdefulla kunder
//                     </p>
//                 </div>
//                 <div style={{
//                     width: '48px',
//                     height: '48px',
//                     background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
//                     borderRadius: '12px',
//                     display: 'flex',
//                     alignItems: 'center',
//                     justifyContent: 'center'
//                 }}>
//                     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ color: '#4338ca' }}>
//                         <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
//                         <path d="M23 21V19C23 18.1645 22.7155 17.3541 22.2094 16.7107C21.7033 16.0673 20.0075 15.6228 19 15.3949" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <path d="M16 3.13C17.0013 3.35715 17.6936 3.80298 18.2094 4.44561C18.7155 5.08825 19 5.89435 19 6.72507C19 7.55579 18.7155 8.36189 18.2094 9.00453C17.7033 9.64716 17.0075 10.093 16 10.32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                     </svg>
//                 </div>
//             </div>

//             {customers.length === 0 ? (
//                 <div style={{
//                     textAlign: 'center',
//                     padding: '3rem 1rem',
//                     background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
//                     borderRadius: '16px',
//                     border: '1px solid #e2e8f0'
//                 }}>
//                     <div style={{
//                         width: '64px',
//                         height: '64px',
//                         borderRadius: '50%',
//                         background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center',
//                         margin: '0 auto 1rem'
//                     }}>
//                         <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-slate-400">
//                             <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                             <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
//                         </svg>
//                     </div>
//                     <p style={{ color: '#64748b', fontSize: '0.875rem', margin: 0 }}>
//                         Inga kunder att visa
//                     </p>
//                 </div>
//             ) : (
//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
//                     {customers.map((customer, index) => (
//                         <div
//                             key={customer.id}
//                             style={{
//                                 background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
//                                 borderRadius: '16px',
//                                 padding: '1.5rem',
//                                 border: '1px solid #e2e8f0',
//                                 transition: 'all 0.3s ease',
//                                 cursor: 'pointer'
//                             }}
//                             onMouseEnter={(e) => {
//                                 e.currentTarget.style.transform = 'translateY(-2px)';
//                                 e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.08)';
//                             }}
//                             onMouseLeave={(e) => {
//                                 e.currentTarget.style.transform = 'translateY(0)';
//                                 e.currentTarget.style.boxShadow = 'none';
//                             }}
//                         >
//                             <div className="d-flex align-items-center justify-content-between mb-3">
//                                 <div className="d-flex align-items-center">
//                                     <div style={{
//                                         width: '48px',
//                                         height: '48px',
//                                         borderRadius: '12px',
//                                         background: getProgressGradient(index),
//                                         display: 'flex',
//                                         alignItems: 'center',
//                                         justifyContent: 'center',
//                                         marginRight: '1rem',
//                                         color: 'white',
//                                         fontWeight: '600',
//                                         fontSize: '0.875rem'
//                                     }}>
//                                         {getInitials(customer.name)}
//                                     </div>
//                                     <div>
//                                         <h6 style={{
//                                             margin: '0 0 0.25rem 0',
//                                             fontWeight: '600',
//                                             color: '#1e293b',
//                                             fontSize: '0.875rem'
//                                         }}>
//                                             {customer.name}
//                                         </h6>
//                                         <div style={{
//                                             fontSize: '0.75rem',
//                                             color: '#64748b',
//                                             display: 'flex',
//                                             alignItems: 'center',
//                                             gap: '0.5rem'
//                                         }}>
//                                             <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
//                                                 <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V7L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                                 <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                                             </svg>
//                                             {customer.invoiceCount} fakturor
//                                         </div>
//                                     </div>
//                                 </div>

//                                 <div className="text-end">
//                                     <div style={{
//                                         fontSize: '0.875rem',
//                                         fontWeight: '700',
//                                         color: '#1e293b',
//                                         marginBottom: '0.25rem'
//                                     }}>
//                                         {formatCurrency(customer.revenue)}
//                                     </div>
//                                     <div style={{
//                                         fontSize: '0.75rem',
//                                         fontWeight: '500',
//                                         padding: '0.25rem 0.5rem',
//                                         borderRadius: '6px',
//                                         background: `${getProgressGradient(index).split(',')[0].split('(')[1]}20`,
//                                         color: getProgressGradient(index).split(',')[0].split('(')[1]
//                                     }}>
//                                         {customer.percentage}% av total
//                                     </div>
//                                 </div>
//                             </div>

//                             {/* Modern Progress Bar */}
//                             <div style={{
//                                 width: '100%',
//                                 height: '8px',
//                                 background: '#f1f5f9',
//                                 borderRadius: '4px',
//                                 overflow: 'hidden'
//                             }}>
//                                 <div style={{
//                                     width: `${customer.percentage}%`,
//                                     height: '100%',
//                                     background: getProgressGradient(index),
//                                     borderRadius: '4px',
//                                     transition: 'width 0.8s ease'
//                                 }} />
//                             </div>
//                         </div>
//                     ))}
//                 </div>
//             )}

//             <div className="text-center mt-4">
//                 <Link
//                     to={`/${companyId}/customers`}
//                     style={{
//                         display: 'inline-flex',
//                         alignItems: 'center',
//                         gap: '0.5rem',
//                         padding: '0.75rem 1.5rem',
//                         background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
//                         color: 'white',
//                         textDecoration: 'none',
//                         borderRadius: '12px',
//                         fontSize: '0.875rem',
//                         fontWeight: '500',
//                         boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
//                         transition: 'all 0.3s ease'
//                     }}
//                     onMouseEnter={(e) => {
//                         e.currentTarget.style.transform = 'translateY(-2px)';
//                         e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
//                     }}
//                     onMouseLeave={(e) => {
//                         e.currentTarget.style.transform = 'translateY(0)';
//                         e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
//                     }}
//                 >
//                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
//                         <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//                         <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
//                     </svg>
//                     Visa alla kunder
//                 </Link>
//             </div>
//         </div>
//     );
// };

// export default TopCustomers;


// // import React, { useState, useEffect } from 'react';
// // import { Link, useParams } from 'react-router-dom';
// // import { ProgressBar } from 'react-bootstrap';
// // import axiosInstance from '../../../../services/AxiosInstance';
// // import { getApiUrl } from '../../../../utils/ApiUtils';

// // const TopCustomers = () => {
// //     const { companyId } = useParams();
// //     const [customers, setCustomers] = useState([
// //         {
// //             id: '1',
// //             name: 'Ework Group AB',
// //             revenue: 125000,
// //             percentage: 45,
// //             invoiceCount: 8
// //         },
// //         {
// //             id: '2',
// //             name: 'ABC Consulting AB',
// //             revenue: 85000,
// //             percentage: 30,
// //             invoiceCount: 5
// //         },
// //         {
// //             id: '3',
// //             name: 'Tech Solutions Sweden',
// //             revenue: 65000,
// //             percentage: 23,
// //             invoiceCount: 4
// //         },
// //         {
// //             id: '4',
// //             name: 'Digital Innovation HB',
// //             revenue: 45000,
// //             percentage: 16,
// //             invoiceCount: 3
// //         },
// //         {
// //             id: '5',
// //             name: 'Svenska Företag AB',
// //             revenue: 35000,
// //             percentage: 12,
// //             invoiceCount: 2
// //         }
// //     ]);

// //     useEffect(() => {
// //         fetchTopCustomers();
// //     }, [companyId]);

// //     const fetchTopCustomers = async () => {
// //         try {
// //             // Replace with actual API call
// //             // const response = await axiosInstance.get(getApiUrl(`${companyId}/dashboard/top-customers`));
// //             // setCustomers(response.data);
// //         } catch (error) {
// //             console.error('Error fetching top customers:', error);
// //         }
// //     };

// //     const formatCurrency = (amount) => {
// //         return new Intl.NumberFormat('sv-SE', {
// //             style: 'currency',
// //             currency: 'SEK',
// //             minimumFractionDigits: 0,
// //             maximumFractionDigits: 0
// //         }).format(amount);
// //     };

// //     const getProgressColor = (index) => {
// //         const colors = ['success', 'info', 'warning', 'danger', 'secondary'];
// //         return colors[index % colors.length];
// //     };

// //     return (
// //         <div className="top-customers">
// //             <h5 className="mb-4">Toppkunder</h5>
// //             {customers.length === 0 ? (
// //                 <div className="text-center py-4">
// //                     <p className="text-muted">Inga kunder att visa</p>
// //                 </div>
// //             ) : (
// //                 <div className="customer-list">
// //                     {customers.map((customer, index) => (
// //                         <div key={customer.id} className="mb-4">
// //                             <div className="d-flex justify-content-between align-items-center mb-2">
// //                                 <div>
// //                                     <h6 className="mb-0">{customer.name}</h6>
// //                                     <small className="text-muted">{customer.invoiceCount} fakturor</small>
// //                                 </div>
// //                                 <div className="text-end">
// //                                     <strong className="text-primary">{formatCurrency(customer.revenue)}</strong>
// //                                     <br />
// //                                     <small className="text-muted">{customer.percentage}%</small>
// //                                 </div>
// //                             </div>
// //                             <ProgressBar
// //                                 now={customer.percentage}
// //                                 variant={getProgressColor(index)}
// //                                 style={{ height: '8px' }}
// //                             />
// //                         </div>
// //                     ))}
// //                 </div>
// //             )}
// //             <div className="text-center mt-3">
// //                 <Link to={`/${companyId}/customers`} className="btn btn-outline-primary btn-sm">
// //                     Visa alla kunder
// //                 </Link>
// //             </div>
// //         </div>
// //     );
// // };

// // export default TopCustomers;